import { Suspense, lazy, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { useLocalAuth } from "@/context/AuthContext";
import { ChevronDown, Menu, Search, ShoppingBag, Truck, User, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { tl } from "@/lib/format";
import { Logo } from "./Logo";
import { MiniCart } from "./MiniCart";

const WhatsAppWidget = lazy(() => import("@/pages/store/InfoPages").then((m) => ({ default: m.WhatsAppWidget })));

const AnnouncementBar = () => {
  const { settings } = useStore();
  return (
    <div className="bg-[hsl(var(--espresso))] text-[hsl(var(--cream))]" data-testid="announcement-bar">
      <div className="container-x flex h-9 items-center justify-between text-xs sm:text-[13px]">
        <p className="flex items-center gap-2 font-medium"><Truck className="h-4 w-4 text-primary" />{(settings?.free_shipping_threshold ?? 3000) > 0 ? `${tl(settings?.free_shipping_threshold ?? 3000)} üzeri siparişlerde kargo bedava!` : "Tüm siparişlerde kargo bedava!"}</p>
        <div className="hidden items-center gap-5 sm:flex">
          <span className="hidden font-display text-sm italic text-primary lg:inline" data-testid="header-tagline">{settings?.tagline || "Tadına Bakmak Serbest ve Helaldir"}</span>
          <Link to="/iletisim" className="hover:text-primary">İletişim</Link>
          <Link to="/banka-hesaplari" className="hover:text-primary">Banka Hesapları</Link>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ testId = "header-search" }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => { e.preventDefault(); navigate(`/urunler${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };
  return (
    <form onSubmit={submit} className="flex w-full items-center rounded-full border-2 border-border bg-muted/60 p-1 transition-colors focus-within:border-primary" data-testid={testId}>
      <Search className="ml-3 h-4 w-4 flex-none text-muted-foreground" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ceviz, antep fıstığı, lokum ara..." data-testid={`${testId}-input`}
        className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground" />
      <button type="submit" data-testid={`${testId}-button`} className="btn-press rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/85">Ara</button>
    </form>
  );
};

const CatLink = ({ c, subs }) => (
  <div className="group relative">
    <NavLink to={`/kategori/${c.slug}`} data-testid={`nav-category-${c.slug}`}
      className={({ isActive }) => `flex items-center gap-1 whitespace-nowrap py-3 text-sm font-semibold xl:text-[15px] transition-colors hover:text-accent ${isActive ? "text-accent" : ""}`}>
      {c.name}{subs.length > 0 && <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />}
    </NavLink>
    {subs.length > 0 && (
      <div className="invisible absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 translate-y-2 rounded-xl border bg-popover p-2 opacity-0 shadow-[0_12px_30px_rgba(42,29,22,0.14)] transition-[opacity,transform,visibility] duration-200 before:absolute before:-top-3 before:left-0 before:h-3 before:w-full group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100" data-testid={`nav-dropdown-${c.slug}`}>
        <Link to={`/kategori/${c.slug}`} className="block rounded-lg px-3 py-2 text-sm font-semibold hover:bg-muted">Tüm {c.name}</Link>
        {subs.map((s) => <Link key={s.slug} to={`/kategori/${s.slug}`} data-testid={`nav-subcategory-${s.slug}`} className="block rounded-lg px-3 py-2 text-sm hover:bg-muted">{s.name}</Link>)}
      </div>
    )}
  </div>
);

const AccountLinks = () => {
  const { token, logout } = useLocalAuth();
  return (
    <>
      <Show when="signed-out">
        {token ? (
          <>
            <Link to="/hesabim" data-testid="header-account" className="flex items-center gap-1.5 text-sm font-medium hover:text-accent"><User className="h-5 w-5" /><span className="hidden md:inline">Hesabım</span></Link>
            <button data-testid="header-logout" onClick={logout} className="hidden text-sm text-muted-foreground hover:text-accent md:inline">Çıkış</button>
          </>
        ) : (
          <Link to="/giris" data-testid="header-sign-in" className="flex items-center gap-1.5 text-sm font-medium hover:text-accent"><User className="h-5 w-5" /><span className="hidden md:inline">Giriş Yap</span></Link>
        )}
      </Show>
      <Show when="signed-in">
        <Link to="/hesabim" data-testid="header-account" className="hidden text-sm font-medium hover:text-accent md:inline">Hesabım</Link>
        <UserButton />
      </Show>
    </>
  );
};

const Header = () => {
  const { count, setCartOpen } = useStore();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  const tops = cats.filter((c) => !c.parent_slug);
  const subsOf = (slug) => cats.filter((c) => c.parent_slug === slug);
  return (
    <header className="sticky top-0 z-40 bg-background shadow-[0_6px_24px_rgba(42,29,22,0.06)]">
      <AnnouncementBar />
      <div className="container-x flex items-center gap-4 py-2 lg:gap-10">
        <button className="lg:hidden" aria-label="Menü" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <Logo />
        <div className="hidden flex-1 md:block"><div className="mx-auto max-w-xl"><SearchBar /></div></div>
        <div className="ml-auto flex items-center gap-4 md:ml-0">
          <AccountLinks />
          <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className="btn-press relative flex items-center gap-2 rounded-full bg-[hsl(var(--espresso))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--cream))] hover:bg-accent" aria-label="Sepet">
            <ShoppingBag className="h-4 w-4" /><span className="hidden sm:inline">Sepet</span>
            {count > 0 && <span data-testid="header-cart-count" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-foreground">{count}</span>}
          </button>
        </div>
      </div>
      <div className="container-x pb-3 md:hidden"><SearchBar testId="mobile-search" /></div>
      <nav className="hidden border-t border-border/60 lg:block" data-testid="category-nav">
        <div className="container-x flex flex-wrap items-center justify-center gap-x-5 xl:gap-x-8">
          <NavLink to="/urunler" data-testid="nav-all-products" className="whitespace-nowrap py-3 text-sm font-semibold xl:text-[15px] hover:text-accent">Tüm Ürünler</NavLink>
          {tops.map((c) => <CatLink key={c.slug} c={c} subs={subsOf(c.slug)} />)}
        </div>
      </nav>
      {open && (
        <nav className="container-x flex max-h-[70vh] flex-col gap-1 overflow-y-auto border-t py-4 lg:hidden" data-testid="mobile-menu">
          <NavLink to="/urunler" className="py-2 font-semibold">Tüm Ürünler</NavLink>
          {tops.map((c) => (
            <div key={c.slug}>
              <NavLink to={`/kategori/${c.slug}`} data-testid={`mobile-nav-${c.slug}`} className="block py-2 font-semibold">{c.name}</NavLink>
              {subsOf(c.slug).map((s) => <NavLink key={s.slug} to={`/kategori/${s.slug}`} className="block py-1.5 pl-4 text-sm text-muted-foreground">{s.name}</NavLink>)}
            </div>
          ))}
          <Link to="/hesabim" className="py-2 font-semibold">Hesabım</Link>
        </nav>
      )}
    </header>
  );
};

const FootCol = ({ title, children }) => (
  <div className="space-y-2.5 text-sm">
    <p className="font-display text-lg font-semibold text-primary">{title}</p>
    {children}
  </div>
);

const Footer = () => {
  const { settings } = useStore();
  const L = ({ to, id, children }) => <Link to={to} data-testid={id} className="block text-[hsl(var(--cream))]/80 transition-colors hover:text-primary">{children}</Link>;
  return (
    <footer className="mt-24 bg-[hsl(var(--espresso))] text-[hsl(var(--cream))]">
      <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="block h-6 w-full -translate-y-full fill-[hsl(var(--espresso))] sm:h-10" aria-hidden="true"><path d="M0 40V20C120 0 240 0 360 14s240 26 360 12S960 0 1080 6s240 26 360 18v16z" /></svg>
      <div className="container-x -mt-6 grid gap-10 pb-14 sm:-mt-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <Logo className="h-24" testId="footer-logo" />
          <p className="mt-4 max-w-sm text-sm text-[hsl(var(--cream))]/75">{settings?.tagline || "1993'ten beri taze kavrulmuş kuruyemiş ve kuru meyve."}</p>
        </div>
        <FootCol title="Alışveriş">
          <L to="/urunler">Tüm Ürünler</L><L to="/sepet">Sepetim</L><L to="/hesabim">Siparişlerim</L><L to="/blog" id="footer-blog">Blog</L>
        </FootCol>
        <FootCol title="Kurumsal">
          <L to="/hakkimizda" id="footer-about">Hakkımızda</L><L to="/iletisim" id="footer-contact">İletişim</L><L to="/banka-hesaplari" id="footer-banks">Banka Hesapları</L>
          <L to="/sayfa/gizlilik-politikasi" id="footer-privacy">Gizlilik Politikası</L><L to="/cerez-politikasi" id="footer-cookies">Çerez Politikası</L><L to="/sayfa/iade-politikasi" id="footer-returns">İade Politikası</L><L to="/sayfa/mesafeli-satis-sozlesmesi" id="footer-distance-sales">Mesafeli Satış Sözleşmesi</L>
        </FootCol>
        <FootCol title="İletişim">
          <p data-testid="footer-phone" className="text-[hsl(var(--cream))]/80">{settings?.contact_phone}</p>
          <p data-testid="footer-email" className="text-[hsl(var(--cream))]/80">{settings?.contact_email}</p>
          <p className="text-[hsl(var(--cream))]/80">Yalnızca Türkiye içi gönderim</p>
        </FootCol>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-[hsl(var(--cream))]/60">
        © {new Date().getFullYear()} {settings?.store_name || "Ceviz Ticaret"} · Tüm fiyatlara KDV dahildir.
      </div>
    </footer>
  );
};

const CookieBanner = () => {
  const [show, setShow] = useState(() => { try { return !localStorage.getItem("cookie_consent"); } catch { return false; } });
  if (!show) return null;
  const accept = () => {
    try { localStorage.setItem("cookie_consent", new Date().toISOString()); } catch {}
    setShow(false);
  };
  return (
    <div role="region" aria-label="Çerez bildirimi" data-testid="cookie-banner"
      className="fixed bottom-3 left-3 right-[84px] z-[45] flex flex-col gap-3 rounded-2xl bg-[hsl(var(--espresso))] p-4 text-sm text-[hsl(var(--cream))] shadow-[0_12px_40px_rgba(0,0,0,0.25)] sm:flex-row sm:items-center sm:pr-5 md:left-4 md:right-auto md:max-w-xl">
      <p className="flex-1 leading-snug">Sitemizde deneyiminizi geliştirmek için çerezler kullanılmaktadır. Detaylı bilgi için <Link to="/cerez-politikasi" className="font-semibold text-primary underline underline-offset-2" data-testid="cookie-banner-link">Çerez Politikamızı</Link> inceleyebilirsiniz.</p>
      <button onClick={accept} data-testid="cookie-banner-accept" className="btn-press flex-none rounded-full bg-primary px-5 py-2 font-semibold text-[hsl(var(--espresso))] hover:bg-[hsl(var(--cream))]">Kabul Et</button>
    </div>
  );
};

export default function StoreLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <MiniCart />
      <Suspense fallback={null}><WhatsAppWidget /></Suspense>
      <CookieBanner />
    </div>
  );
}
